import * as React from 'react'
import { connect } from 'react-redux'
import type { RouteComponentProps } from 'react-router'
import { Callout } from '@blueprintjs/core'
import WithNotFound from '../../../../components/AppError/WithNotFound'
import Errors from '../../../../components/Errors'
import ImageUploader from '../../../../components/images/Uploader'
import Spinner from '../../../../components/Spinner'
import Widget from '../../../../components/Widget'
import { AppState } from '../../../../store'
import { ShopifyFormV4 } from './ShopifyFormV4'
import { UprightStyle } from '../../../../types'
import { onVariantChangeHandler } from '../../../products_v5/components/JoinedForm/types'
import { generateVariantTitles } from '../../../products_v5/components/JoinedForm/utils'
import { newVariantFromProduct } from '../../../products_v5/utils'
import { getShopifyTags, getShopifyRequiredTags } from '../../../../utils/tags'
import { PICKUP_CARRIER } from '../../../../constants/orders/Carriers'

interface Props {
  parentSetState: any
  isNewProductListing?: boolean
  loading: boolean
  errors: any
  images: any
  product: any
  listingId: any
  form: any
  onListingStrategyChange: () => void
  listingStrategyDisabled: boolean
  externalListingDeleted: boolean
  shopifySettings: any
  fetchListing?: () => void
  onSkuParseLoading?: (loading: boolean) => void
}

const style: UprightStyle = {
  inactiveCallout: {
    marginBottom: 20
  }
}

// A parent component is responsible for the simple state management of form field values, form submission, etc.
// This is responsible for state management:
// - which is localized to certain fields (such as making API calls to fetch possible values)
// - involving interactions between fields (such as debouncing specifics when the category changes)
// - basically everything that we don't need to pull into the higher level which handles form submission
class ShopifyListingForm extends React.Component<Props & RouteComponentProps<{ id?: string; listingId?: string }>> {
  onFormChange = ({ name, value }) => {
    this.props.parentSetState(s => ({
      form: {
        ...s.form,
        [name]: value
      }
    }))
  }

  onVariantChange: onVariantChangeHandler = (idx, key, value) => {
    this.props.parentSetState(s => ({
      product: {
        ...s.product,
        variants: s.product.variants.map((v, i) => {
          if (i === idx) {
            return {
              ...v,
              [key]: value
            }
          }

          return v
        })
      }
    }))
  }

  onProductChange = (key, value) => {
    if (key === 'optionIdsToDestroy') {
      this.props.parentSetState({
        optionIdsToDestroy: value
      })
      return
    }

    if (key === 'options') {
      const newVariantTitles = generateVariantTitles(value.map(o => o.values))
      const oldVariants = this.props.product.variants

      const newVariants = newVariantTitles.map(vt => {
        const foundIdx = oldVariants.findIndex(v => v.title === vt)
        if (foundIdx >= 0) {
          return oldVariants.splice(foundIdx, 1)[0]
        }

        return {
          ...newVariantFromProduct(this.props.product, vt),
          // Use the existing listing form price for this page only, which sometimes may be the shopify settings default price from:
          // https://github.com/uprightlabs/lister/blob/fed1edf9ab100a1846aaec76966feff2bb827fed/client/routes/products_v4/listings/shopify/index.tsx#L290
          // or
          // https://github.com/uprightlabs/lister/blob/fed1edf9ab100a1846aaec76966feff2bb827fed/app/models/listings/shopify.rb#L178
          price: this.props.form?.price
        }
      })

      this.props.parentSetState(s => ({
        product: {
          ...s.product,
          [key]: value,
          variants: newVariants
        },
        variantIdsToDestroy: [...s.variantIdsToDestroy, ...oldVariants.map(v => v.id)]
      }))
      return
    }

    this.props.parentSetState(s => {
      const form =
        key === 'carrierId'
          ? {
              ...s.form,
              locationId:
                value === PICKUP_CARRIER?.id
                  ? s.shopifySettings?.defaultPickupLocationId
                  : s.shopifySettings?.defaultShippingLocationId
            }
          : s.form
      return {
        product: {
          ...s.product,
          [key]: value
        },
        form
      }
    })
  }

  onCategoryChange = e => {
    const shopifyTags = getShopifyTags(
      this.props.form.tags,
      this.props.shopifySettings.productCategoryAsTags,
      this.props.product.category?.pathCache,
      e.label
    )

    const shopifyRequiredTags = getShopifyRequiredTags(this.props.shopifySettings.productCategoryAsTags, e.label)

    this.props.parentSetState(s => ({
      product: {
        ...s.product,
        categoryId: e.value,
        // update the pathCache string for the selected category
        category: { ...s.product.category, pathCache: e.label }
      },
      form: {
        ...s.form,
        tags: shopifyTags,
        requiredTags: shopifyRequiredTags
      }
    }))
  }

  onImageChange = (images, cb: (any) => void = () => {}) => {
    this.props.parentSetState(
      s => ({
        images: images.filter(i => !i.removed),
        deletedImages: s.deletedImages.concat(images.filter(i => i.removed))
      }),
      () => cb(this.props.images)
    )
  }

  render() {
    if (this.props.loading) {
      return <Spinner />
    }

    return (
      <>
        <Errors error={this.props.errors} />
        {this.props.externalListingDeleted && (
          <Callout intent="warning" title="Item is inactive" style={style.inactiveCallout}>
            <p>
              This item is not currently listed to Shopify. Any edits made to this listing will be pushed up on relist.
            </p>
          </Callout>
        )}
        <Widget title="Images">
          <ImageUploader images={this.props.images} onChange={this.onImageChange} />
        </Widget>
        <Widget title="Shopify">
          <ShopifyFormV4
            onVariantChange={this.onVariantChange}
            syncProductTags={this.props.shopifySettings.syncProductTags}
            form={this.props.form}
            onFormChange={this.onFormChange}
            errors={this.props.errors && this.props.errors.errors}
            product={this.props.product}
            onProductChange={this.onProductChange}
            onCategoryChange={this.onCategoryChange}
            onListingStrategyChange={this.props.onListingStrategyChange}
            listingStrategyDisabled={!!this.props.listingId || this.props.isNewProductListing}
            fetchListing={this.props.fetchListing}
            onSkuParseLoading={this.props.onSkuParseLoading}
          />
        </Widget>
      </>
    )
  }
}

export const ShopifyListing = connect((state: AppState) => ({ current: state.current }))(
  WithNotFound(ShopifyListingForm)
)
